// MediaSelection.js
import React, { useState } from "react";
import styled from "styled-components";
import Tabs from "../../../Tabs";
import Upload from "../../Shared/UploadMedia";
import MediaBrowser from "../MediaBrowser";
import EmbedURL from "../../Shared/EmbedURL";
import EmbedCode from "../EmbedCode";
import AudioLinkPaste from "../../Shared/AudioLinkPaste";
import RecentMedia from "../RecentMedia";
import { MEDIA_LIBRARY_PICKER_ENABLED } from "../../../../constants/featureFlags";
import { MEDIA_RECORDER_ENABLED } from "../../../../constants/featureFlags";
import { STOCK_IMAGE_LIBRARY_ENABLED } from "../../../../constants/featureFlags";
import MediaRecorder from "../../../MediaCapture";
import MediaLibraryPickerConnected from "../../../MediaLibrary/MediaLibraryPickerConnected";
import StockImageLibrary from "../../../MediaLibrary/StockImageLibrary";
import { isStockEligibleType } from "../../../MediaLibrary/stockImageLibrary.logic";
import { IconLibrary } from "../../../MediaLibrary/icons";
import { acceptedTypesForBuilder, toInsertValue } from "./librarySourceLogic";
import tokens from "../../../../theme/tokens";
import { tabsVariantFor } from "../../../Tabs/tabsVariant";
import { orderSourceTabs, SOURCE_STRIP_SX } from "./sourceTabOrder";
import { FEATURES } from "../../../../constants/features";
import { useIfUserEligible } from "../../../../hooks/useIfUserEligible";
import TierFeatureBadge from "../../../TierFeatureBadge";
import FeatureEligibility from "../../../FeatureEligibility";
import { CircularProgress } from "@mui/material";

// LIB-image-library: the curated preset-image source (flag-gated, image-only).
// Display label "Preset" (renamed from "Stock" 2026-08-18, ISP-9999). STOCK_TAB is
// both the rendered label and the panel-match key, so this one constant renames the
// tab and keeps its StockImageLibrary panel wired.
export const STOCK_TAB = "Preset";

// CAP-A11Y-01 (WCAG 2.4.7): the picker tab strip had NO visible keyboard-focus
// indicator ([role=tab]:focus-visible resolved to outline:0 / box-shadow:none),
// while the adjacent "Reuse from your Media Library" control already shows one.
// Add the app's STANDARD focus ring (design-system token `shadow.focus`,
// 0 0 0 3px #CBB4FE == PrimaryPurple300 — the same ring every MediaLibrary
// control uses) to the picker's tabs. Passed via `sx` so it is SCOPED to this
// picker only (every other Tabs consumer is byte-identical) and ADDITIVE — the
// selector targets ONLY the keyboard-focus states, never the resting/selected
// appearance, and uses a distinct key so it does not clobber the shared Tabs
// component's own `& .MuiTab-root` sizing block. Since this MediaSelection
// picker is injected into ~15 consumer surfaces (Image/Video/Audio/Document
// blocks, Portrait, Wallpaper, …), one additive change fixes them all safely.
const PICKER_TAB_FOCUS_RING_SX = {
  // T22.146: universal strip layout (wraps, never clips) — see sourceTabOrder.js.
  ...SOURCE_STRIP_SX,
  "& .MuiTab-root.Mui-focusVisible, & .MuiTab-root:focus-visible": {
    outline: "none",
    boxShadow: tokens.shadow.focus,
    // keep the ring from being clipped by the MUI Tabs scroller's overflow
    zIndex: 1,
    borderRadius: `${tokens.radius.md}px`,
  },
};

// DL-048 (Gordon universal-access addendum 2026-07-14): the "Reuse from your Media
// Library" affordance is mounted HERE, at the shared layer, so every MediaSelection
// consumer (Segment blocks, Portrait, wallpaper/segment-image, Business Box, gallery
// details, …) inherits the reuse modal for free — never a per-surface reimplementation.
// Flag-gated, default OFF: when OFF nothing below renders and the tab strip + panels
// are byte-identical to base.
const ReuseLibraryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 12px;
  padding: 7px 14px;
  min-height: 32px;
  border: 1px solid #9b5af8;
  border-radius: 8px;
  background: #fbfaff;
  color: #6f27d6;
  font: 600 13px "DM Sans", system-ui, sans-serif;
  cursor: pointer;
  transition: background 120ms ease;
  & svg {
    flex: 0 0 auto;
  }
  &:hover {
    background: #efe9fe;
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(155, 90, 248, 0.35);
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const MediaSelection = ({
  src,
  handleChange,
  handleRemove,
  handleTitle,
  type,
  defaultEmbedValue,
  categories = ["Upload", "Embed URL", "Browse", "Recent"],
  isBussinessBox = false,
  media,
  setMedia,
  isGifAllowed = false,
  showRecentDropdown = true,
  showEmbedDisclaimer = false,
  onEmbedURLChange = null,
  // DL-048 modal: consumers MAY declare accepted media types + the open Segment
  // name; by default we derive acceptedTypes from this picker's existing `type`
  // restriction (image/video/audio/document) so no consumer edit is needed.
  acceptedTypes = null,
  segmentName = null,
  // LIB-image-library (NEM F4): opt-out escape hatch. Default TRUE so every image
  // consumer keeps the curated Stock source when the flag is ON — but a surface
  // where curated stock makes no sense (e.g. a legal DMCA takedown/counter form)
  // can pass showStock={false} to suppress it explicitly, without relying only on
  // it happening to pass empty categories. (DMCA forms already pass categories={[]}
  // so they never showed the tab; this makes the intent declarable, not incidental.)
  showStock = true,
  // kelly-1005-P1-portrait-video-length-before-upload: opt-in video length cap,
  // threaded to BOTH the Upload tab (UploadMedia) and the Record tab
  // (MediaRecorder) so "Same tabs as the Video block" also means "same rule" —
  // undefined on every existing caller keeps it byte-identical.
  videoMaxDurationSeconds,
  videoMaxBytes,
  videoDurationContextLabel = "Videos",
}) => {
  // T22.146: whatever order the caller lists its sources in, the strip shows
  // Upload · Embed URL · Browse · Preset · Recent, and opens on its first tab.
  const orderedCategories = orderSourceTabs(categories);
  const [selectedCategory, setSelectedCategory] = useState(
    orderedCategories?.[0]
  );
  const [pickerOpen, setPickerOpen] = useState(false);

  // gordon-0915-video-block-media-pane-pixabay-embed-first-record-tier:
  // Record is an Essential-tier item exactly like Upload, gated the SAME way
  // — same feature key, same upsell card, same (E) badge. This mirrors
  // UploadMedia/index.jsx's own internal gate exactly (`type === "video" ?
  // FEATURES.UPLOAD_VIDEOS : null`) — video is the only type Upload itself
  // gates today, so it's the only type Record gains a NEW gate for here.
  const {
    isEligible: isVideoUploadEligible,
    subscription: videoUploadSubscription,
    isCheckingEligibility: isCheckingVideoUploadEligibility,
    requiredSubscription: videoUploadRequiredSubscription,
  } = useIfUserEligible(type === "video" ? FEATURES.UPLOAD_VIDEOS : null);

  // Accept exactly what this surface already restricts to (never invent a type).
  const resolvedAcceptedTypes = acceptedTypes ?? acceptedTypesForBuilder(type);

  // LIB-image-library: append the curated "Preset" source ONLY where tabs already
  // show AND this surface accepts images. Flag OFF -> no "Preset" tab, StockImageLibrary
  // never mounts.
  const showStockTab =
    STOCK_IMAGE_LIBRARY_ENABLED &&
    showStock &&
    isStockEligibleType(type) &&
    categories?.length > 0;
  // The Preset source is additive AND ordered: it is inserted immediately BEFORE the
  // "Recent" tab so "Recent" always stays the LAST tab (product order
  // Upload · Embed URL · Browse · Preset · Recent). When the flag is OFF (or categories
  // is empty/undefined) effectiveCategories === caller's categories -> byte-identical.
  const effectiveCategories = (() => {
    if (!orderedCategories?.length) return orderedCategories;
    if (!showStockTab) return orderedCategories;
    return orderSourceTabs([...orderedCategories, STOCK_TAB]);
  })();

  const handleCategoryChange = (event, newValue) => {
    event.stopPropagation();
    setSelectedCategory(newValue);
  };

  // Only build the icon-bearing tab list (a different, Stack-wrapped Tab
  // render path in the shared Tabs component) when there is an actual badge
  // to show — every OTHER consumer (non-video, or video-but-eligible) keeps
  // passing plain `categories` strings and stays byte-identical.
  const showVideoTierBadges =
    type === "video" && !isVideoUploadEligible && !isCheckingVideoUploadEligibility;
  const categoriesWithIcons = showVideoTierBadges
    ? effectiveCategories.map((category) => {
        const config = { label: category, value: category };
        if (category === "Upload" || category === "Record") {
          config.customIcon = (
            <TierFeatureBadge
              requiredSubscription={videoUploadRequiredSubscription}
              subscription={videoUploadSubscription}
              placement="bottom"
            />
          );
        }
        return config;
      })
    : null;

  return (
    <>
      {effectiveCategories?.length > 0 && (
        <Tabs
          categories={categoriesWithIcons ? undefined : effectiveCategories}
          categoriesWithIcons={categoriesWithIcons ?? undefined}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          variant={tabsVariantFor(effectiveCategories)}
          sx={PICKER_TAB_FOCUS_RING_SX}
        />
      )}
      {/* T22.139: "Reuse from your Media Library" belongs to the Upload tab
          (drop zone + choose file + reuse); a strip with no Upload tab keeps it
          above every panel as before. */}
      {MEDIA_LIBRARY_PICKER_ENABLED &&
        categories?.length > 0 &&
        (selectedCategory === "Upload" || !categories.includes("Upload")) && (
          <ReuseLibraryBtn
            type="button"
            onClick={() => setPickerOpen(true)}
            data-testid="open-media-library"
          >
            <IconLibrary size={16} /> Reuse from your Media Library
          </ReuseLibraryBtn>
        )}
      {/* T22.139: the drop zone renders on the Upload tab itself — it used to
          follow categories[0], so the GIF fill (Browse first) showed it under
          Browse and left Upload empty. */}
      {(selectedCategory === "Upload" || !categories?.length) && (
        <Upload
          src={src}
          handleMedia={(files) => handleChange(type, files)}
          handleTitle={handleTitle}
          type={type}
          isBusinessBox={isBussinessBox}
          media={media}
          setMedia={setMedia}
          videoMaxDurationSeconds={videoMaxDurationSeconds}
          videoMaxBytes={videoMaxBytes}
          videoDurationContextLabel={videoDurationContextLabel}
        />
      )}
      {selectedCategory === "Embed URL" &&
        type !== "audio" &&
        categories?.length > 1 && (
          <EmbedURL
            defaultValue={defaultEmbedValue}
            type={type}
            onConfirm={(value) => handleChange(type, value)}
            onError={handleRemove}
            pathType={isBussinessBox ? "bussinessBox" : "wheel"}
            media={media}
            showDisclaimer={showEmbedDisclaimer}
            onExternalChange={
              isBussinessBox ? (value) => onEmbedURLChange(value) : null
            }
          />
        )}
      {selectedCategory === "Embed Code" && type === "audio" && (
        <EmbedCode
          onConfirm={(value) => handleChange(type, value)}
          media={media}
        />
      )}
      {selectedCategory === "Link" && type === "audio" && (
        <AudioLinkPaste
          onConfirm={(value) => handleChange(type, value)}
          media={media}
        />
      )}
      {MEDIA_RECORDER_ENABLED &&
        selectedCategory === "Record" &&
        (type === "video" ? (
          isCheckingVideoUploadEligibility ? (
            <CircularProgress />
          ) : isVideoUploadEligible ? (
            <MediaRecorder
              mode="video"
              onComplete={(value) => handleChange(type, value)}
              maxDurationMs={
                videoMaxDurationSeconds ? videoMaxDurationSeconds * 1000 : undefined
              }
            />
          ) : (
            // h5718ffb7-style paywall bypass, closed here too: Record must be
            // gated identically to Upload (UploadMedia/index.jsx), never
            // mounted unconditionally.
            <FeatureEligibility
              subscription={videoUploadSubscription}
              requiredSubscription={videoUploadRequiredSubscription}
            />
          )
        ) : (
          <MediaRecorder
            mode={type === "audio" ? "audio" : "video"}
            onComplete={(value) => handleChange(type, value)}
          />
        ))}
      {selectedCategory === "Browse" && (
        <MediaBrowser
          mediaType={type}
          onConfirm={(value) => handleChange(type, value)}
          isGifAllowed={isGifAllowed}
        />
      )}
      {selectedCategory === "Recent" && (
        <RecentMedia
          type={type}
          onClickMedia={(value) => handleChange(type, value)}
          showDropdown={showRecentDropdown}
        />
      )}
      {showStockTab && selectedCategory === STOCK_TAB && (
        <StockImageLibrary
          selectedSrc={src}
          onSelect={(value) => handleChange(type, value)}
        />
      )}
      {MEDIA_LIBRARY_PICKER_ENABLED && (
        <MediaLibraryPickerConnected
          open={pickerOpen}
          acceptedTypes={resolvedAcceptedTypes}
          segmentName={segmentName}
          onClose={() => setPickerOpen(false)}
          onInsert={(item) => {
            handleChange(type, toInsertValue(item));
            setPickerOpen(false);
          }}
        />
      )}
    </>
  );
};

export default MediaSelection;
